import { MouseState, KeyState, CONSTANTS } from './types';

export type MouseEventHandler = (x: number, y: number) => void;
export type KeyEventHandler = (key: string) => void;
export type DragEventHandler = (startX: number, startY: number, currentX: number, currentY: number) => void;
export type DragEndEventHandler = (startX: number, startY: number, endX: number, endY: number) => void;
export type WheelEventHandler = (x: number, y: number, deltaY: number) => void;

export class Input {
  private canvas: HTMLCanvasElement;
  private mouse: MouseState = { x: 0, y: 0, isDown: false };
  private keys: KeyState = {};
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private isDragging: boolean = false;
  private dragThreshold: number = 5; // pixels before considered dragging

  private clickHandler?: MouseEventHandler;
  private keyDownHandler?: KeyEventHandler;
  private dragHandler?: DragEventHandler;
  private dragEndHandler?: DragEndEventHandler;
  private wheelHandler?: WheelEventHandler;

  // Mobile text input support
  private mobileInput: HTMLInputElement | null = null;
  private mobileInputActive: boolean = false;
  private boundMobileInputHandler: () => void;

  // Store bound handlers for proper cleanup
  private boundHandleClick: (e: MouseEvent) => void;
  private boundHandleMouseDown: (e: MouseEvent) => void;
  private boundHandleMouseUp: (e: MouseEvent) => void;
  private boundHandleMouseMove: (e: MouseEvent) => void;
  private boundHandleKeyDown: (e: KeyboardEvent) => void;
  private boundHandleKeyUp: (e: KeyboardEvent) => void;
  private boundHandleWheel: (e: WheelEvent) => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // Bind handlers once for proper cleanup
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleMouseDown = this.handleMouseDown.bind(this);
    this.boundHandleMouseUp = this.handleMouseUp.bind(this);
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleKeyUp = this.handleKeyUp.bind(this);
    this.boundHandleWheel = this.handleWheel.bind(this);
    this.boundMobileInputHandler = this.handleMobileInput.bind(this);

    // Setup mobile text input
    this.mobileInput = document.getElementById('mobile-text-input') as HTMLInputElement;
    if (this.mobileInput) {
      this.mobileInput.addEventListener('input', this.boundMobileInputHandler);
      this.mobileInput.addEventListener('keydown', (e: KeyboardEvent) => {
        // Forward Enter and Escape directly since the window keydown handler
        // is skipped while mobile input is active. These keys don't change
        // the input value so they won't be caught by the 'input' event.
        // Backspace is NOT forwarded here — it's handled via the 'input' event
        // and the __mobileInput: sync path to avoid double-processing.
        if (e.key === 'Enter' || e.key === 'Escape') {
          this.keyDownHandler?.(e.key);
          e.preventDefault();
        }
      });
    }

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Mouse events
    this.canvas.addEventListener('click', this.boundHandleClick);
    this.canvas.addEventListener('mousedown', this.boundHandleMouseDown);
    this.canvas.addEventListener('mouseup', this.boundHandleMouseUp);
    this.canvas.addEventListener('mousemove', this.boundHandleMouseMove);
    this.canvas.addEventListener('wheel', this.boundHandleWheel, { passive: false });

    // Keyboard events (on window to capture when canvas is focused)
    window.addEventListener('keydown', this.boundHandleKeyDown);
    window.addEventListener('keyup', this.boundHandleKeyUp);
  }

  private getCanvasCoordinates(event: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = CONSTANTS.CANVAS_WIDTH / rect.width;
    const scaleY = CONSTANTS.CANVAS_HEIGHT / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  private handleClick(event: MouseEvent): void {
    const coords = this.getCanvasCoordinates(event);
    console.log(`Click at (${coords.x}, ${coords.y})`);
    this.clickHandler?.(coords.x, coords.y);
  }

  private handleMouseDown(event: MouseEvent): void {
    this.mouse.isDown = true;
    const coords = this.getCanvasCoordinates(event);
    this.mouse.x = coords.x;
    this.mouse.y = coords.y;
    this.dragStartX = coords.x;
    this.dragStartY = coords.y;
    this.isDragging = false;
  }

  private handleMouseUp(event: MouseEvent): void {
    const coords = this.getCanvasCoordinates(event);
    if (this.isDragging && this.dragEndHandler) {
      this.dragEndHandler(this.dragStartX, this.dragStartY, coords.x, coords.y);
    }
    this.mouse.isDown = false;
    this.isDragging = false;
  }

  private handleMouseMove(event: MouseEvent): void {
    const coords = this.getCanvasCoordinates(event);
    this.mouse.x = coords.x;
    this.mouse.y = coords.y;

    // Check if we should start dragging
    if (this.mouse.isDown && !this.isDragging) {
      const dx = coords.x - this.dragStartX;
      const dy = coords.y - this.dragStartY;
      if (Math.sqrt(dx * dx + dy * dy) > this.dragThreshold) {
        this.isDragging = true;
      }
    }

    // Notify drag handler
    if (this.isDragging && this.dragHandler) {
      this.dragHandler(this.dragStartX, this.dragStartY, coords.x, coords.y);
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    this.keys[event.key] = true;
    console.log(`Key down: ${event.key}`);
    // When mobile input is active, keys are handled via the mobile input's
    // own keydown/input listeners to avoid double-processing
    if (this.mobileInputActive) return;
    this.keyDownHandler?.(event.key);
  }

  private handleKeyUp(event: KeyboardEvent): void {
    this.keys[event.key] = false;
  }

  private handleWheel(event: WheelEvent): void {
    const coords = this.getCanvasCoordinates(event);
    if (this.wheelHandler) {
      event.preventDefault();
      this.wheelHandler(coords.x, coords.y, event.deltaY);
    }
  }

  onMouseClick(handler: MouseEventHandler): void {
    this.clickHandler = handler;
  }

  onKeyDown(handler: KeyEventHandler): void {
    this.keyDownHandler = handler;
  }

  onDrag(handler: DragEventHandler): void {
    this.dragHandler = handler;
  }

  onDragEnd(handler: DragEndEventHandler): void {
    this.dragEndHandler = handler;
  }

  onWheel(handler: WheelEventHandler): void {
    this.wheelHandler = handler;
  }

  getMouseState(): MouseState {
    return { ...this.mouse };
  }

  getDragState(): { isDragging: boolean; startX: number; startY: number; currentX: number; currentY: number } {
    return {
      isDragging: this.isDragging,
      startX: this.dragStartX,
      startY: this.dragStartY,
      currentX: this.mouse.x,
      currentY: this.mouse.y,
    };
  }

  isKeyDown(key: string): boolean {
    return this.keys[key] ?? false;
  }

  // Mobile text input support - call to focus the hidden input and trigger virtual keyboard
  focusMobileInput(currentValue: string = '', inputMode: 'text' | 'numeric' = 'text'): void {
    if (!this.mobileInput) return;
    this.mobileInputActive = true;
    this.mobileInput.inputMode = inputMode;
    this.mobileInput.value = currentValue;
    // Move into view so mobile browsers allow focus
    this.mobileInput.style.left = '0';
    this.mobileInput.style.opacity = '0';
    this.mobileInput.style.pointerEvents = 'auto';
    this.mobileInput.focus();
    // Ensure cursor is at the end so new characters are appended, not prepended.
    // Some mobile browsers reset cursor to position 0 after programmatic .value set.
    const len = currentValue.length;
    this.mobileInput.setSelectionRange(len, len);
  }

  blurMobileInput(): void {
    if (!this.mobileInput) return;
    this.mobileInputActive = false;
    this.mobileInput.blur();
    this.mobileInput.style.left = '-9999px';
    this.mobileInput.style.pointerEvents = 'none';
  }

  isMobileInputActive(): boolean {
    return this.mobileInputActive;
  }

  private handleMobileInput(): void {
    if (!this.mobileInput || !this.mobileInputActive) return;
    // Synthesize key events from input changes so screens get them via keyDownHandler
    const value = this.mobileInput.value;
    // Send the full value as a special key so screens can sync
    this.keyDownHandler?.(`__mobileInput:${value}`);
  }

  destroy(): void {
    this.canvas.removeEventListener('click', this.boundHandleClick);
    this.canvas.removeEventListener('mousedown', this.boundHandleMouseDown);
    this.canvas.removeEventListener('mouseup', this.boundHandleMouseUp);
    this.canvas.removeEventListener('mousemove', this.boundHandleMouseMove);
    this.canvas.removeEventListener('wheel', this.boundHandleWheel);
    window.removeEventListener('keydown', this.boundHandleKeyDown);
    window.removeEventListener('keyup', this.boundHandleKeyUp);
    if (this.mobileInput) {
      this.mobileInput.removeEventListener('input', this.boundMobileInputHandler);
    }
  }
}
